import {
  useState, useCallback, useEffect, useRef
} from 'react';

export function useOverflowMenu(initialItems, activeItem = null) {
  const [visibleItems, setVisibleItems] = useState(initialItems);
  const [overflowItems, setOverflowItems] = useState([]);
  const [pinnedItem, setPinnedItem] = useState(activeItem);

  const visibleRef = useRef(visibleItems);
  const overflowRef = useRef(overflowItems);
  const pinnedRef = useRef(pinnedItem);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const logoRef = useRef(null);
  const rafId = useRef(null);
  const lastCommittedRef = useRef({ visible: [], overflow: [] });
  const itemWidthsRef = useRef(new Map());

  // SYNC Refs
  useEffect(() => { visibleRef.current = visibleItems; }, [visibleItems]);
  useEffect(() => { overflowRef.current = overflowItems; }, [overflowItems]);
  useEffect(() => { pinnedRef.current = pinnedItem; }, [pinnedItem]);

  const getAvailableSpace = useCallback(() => {
    const left = leftRef.current?.getBoundingClientRect();
    const right = rightRef.current?.getBoundingClientRect();
    const logo = logoRef.current?.getBoundingClientRect();

    if (!left || !right || !logo) return null;
    if (!left.width || !right.width || !logo.width) return null;

    return {
      leftWidth: left.width,
      available: window.innerWidth - (right.width + logo.width) - 58,
    };
  }, []);

  const sortByInitialOrder = useCallback((items) => [...items].sort(
    (a, b) => initialItems.findIndex((i) => i.path === a.path)
      - initialItems.findIndex((i) => i.path === b.path)
  ), [initialItems]);

  const applyPinnedOrdering = useCallback((visible) => {
    if (!pinnedRef.current || !pinnedRef.current.forceVisible) return visible;

    const pinnedOriginalIndex = initialItems.findIndex(
      (i) => i.path === pinnedRef.current.path
    );

    const currentIndex = visible.findIndex((v) => v.path === pinnedRef.current.path);
    if (currentIndex === -1) return visible;

    const outOfOrderIndex = visible.findIndex((item) => {
      const o = initialItems.findIndex((i) => i.path === item.path);
      return o < pinnedOriginalIndex && item.path !== pinnedRef.current.path;
    });

    if (outOfOrderIndex !== -1 && currentIndex < outOfOrderIndex) {
      const updated = [...visible];
      updated.splice(currentIndex, 1);
      updated.splice(outOfOrderIndex, 0, pinnedRef.current);
      return updated;
    }

    return visible;
  }, [initialItems]);

  const reconcileLists = useCallback(
    (visible, overflow) => {
      const seen = {};

      const dedupVisible = visible.filter((item) => {
        if (seen[item.path]) return false;
        seen[item.path] = true;
        return true;
      });

      const dedupOverflow = overflow.filter((item) => {
        if (seen[item.path]) return false;
        seen[item.path] = true;
        return true;
      });

      const missing = initialItems.filter((item) => !seen[item.path]);

      return {
        visible: dedupVisible.concat(missing),
        overflow: dedupOverflow,
      };
    },
    [initialItems]
  );

  const listsEqual = (a, b) => a.length === b.length
    && a.every((x, i) => x.path === b[i].path
    && x.forceVisible === b[i].forceVisible);

  const commitLists = useCallback((newVisible, newOverflow) => {
    const { visible, overflow } = reconcileLists(newVisible, newOverflow);

    const last = lastCommittedRef.current;

    if (
      listsEqual(last.visible, visible)
      && listsEqual(last.overflow, overflow)
    ) {
      return;
    }

    lastCommittedRef.current = { visible, overflow };

    setVisibleItems(visible);
    setOverflowItems(overflow);
  }, [reconcileLists]);

  const applyForceVisibleFlags = useCallback((selected) => {
    // Work from refs to avoid races and then commit
    const mapper = (item) => ({
      ...item,
      forceVisible: item.path === selected.path,
    });

    const newVisible = visibleRef.current.map(mapper);
    const newOverflow = overflowRef.current.map(mapper);
    commitLists(newVisible, newOverflow);
  }, [commitLists]);

  const finalizeVisible = useCallback((items) => {
    const ordered = sortByInitialOrder(items);
    return applyPinnedOrdering(ordered);
  }, [sortByInitialOrder, applyPinnedOrdering]);

  const selectOverflowItem = useCallback(
    (route) => {
      const visible = [...visibleRef.current];
      const overflow = [...overflowRef.current];
      const selected = { ...route, forceVisible: true };

      const idx = overflow.findIndex((r) => r.path === selected.path);
      if (idx !== -1) overflow.splice(idx, 1);

      // Demote previous pinned item WITHOUT moving it
      if (pinnedRef.current && pinnedRef.current.path !== selected.path) {
        const prevPinnedIndex = visible.findIndex(
          (v) => v.path === pinnedRef.current.path
        );
        if (prevPinnedIndex !== -1) {
          visible[prevPinnedIndex] = {
            ...visible[prevPinnedIndex],
            forceVisible: false,
          };
        }
      }

      setPinnedItem(selected);
      applyForceVisibleFlags(selected);

      // Insert selected into visible at its original index
      const initialIndex = initialItems.findIndex(
        (i) => i.path === selected.path
      );
      let insertIndex = visible.findIndex((v) => {
        const i = initialItems.findIndex((item) => item.path === v.path);
        return i > initialIndex;
      });
      if (insertIndex === -1) insertIndex = visible.length;

      visible.splice(insertIndex, 0, selected);

      const finalVisible = finalizeVisible(visible);
      commitLists(finalVisible, [...overflow]);

      requestAnimationFrame(() => {
        const fitVisible = [...visibleRef.current];
        const fitOverflow = [...overflowRef.current];

        while (true) {
          const space = getAvailableSpace();
          if (!space) return;

          if (space.leftWidth <= space.available) break;

          const removableIndex = fitVisible
            .map((item, i) => ({
              item,
              i,
              priority: initialItems.findIndex(
                (orig) => orig.path === item.path
              ),
            }))
            .filter(({ item }) => !item.forceVisible
              && (!pinnedRef.current || pinnedRef.current.path !== item.path))
            .sort((a, b) => b.priority - a.priority)[0]?.i;

          if (removableIndex == null) break;

          const removed = fitVisible.splice(removableIndex, 1)[0];
          fitOverflow.unshift(removed);

          commitLists(finalizeVisible(fitVisible), fitOverflow);
        }
      });
    },
    [initialItems, applyForceVisibleFlags, commitLists, finalizeVisible, getAvailableSpace]
  );

  const performCheck = useCallback(() => {
    const space = getAvailableSpace();
    if (!space) return;

    const { leftWidth, available } = space;

    // Push to overflow
    if (available <= leftWidth) {
      const visible = [...visibleRef.current];
      const overflow = [...overflowRef.current];

      const removableIndex = [...visible]
        .map((x, i) => ({ item: x, i }))
        .reverse()
        .find(
          ({ item }) => !item.forceVisible
            && (!pinnedRef.current || item.path !== pinnedRef.current.path)
        )?.i;

      if (removableIndex == null) return;

      const removed = visible.splice(removableIndex, 1)[0];
      overflow.unshift(removed);

      commitLists(visible, overflow);

      requestAnimationFrame(() => performCheck());
      return;
    }

    const visible = [...visibleRef.current];
    const overflow = [...overflowRef.current];

    while (overflow.length > 0) {
      const next = overflow[0];
      const nextWidth = itemWidthsRef.current.get(next.path);

      if (!nextWidth) break;

      const nextSpace = getAvailableSpace();
      if (!nextSpace) break;

      if (nextSpace.leftWidth + nextWidth <= nextSpace.available) {
        visible.push(next);
        overflow.shift();
      } else {
        break;
      }
    }

    commitLists(finalizeVisible(visible), overflow);
  }, [getAvailableSpace, finalizeVisible, commitLists]);

  const checkOverlap = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      performCheck();
    });
  }, [performCheck]);

  const selectVisibleItem = useCallback((route) => {
    const selected = { ...route, forceVisible: true };

    setPinnedItem(selected);

    // IMMEDIATELY enforce forceVisible BEFORE any resize logic
    const visible = visibleRef.current.map((item) => ({
      ...item,
      forceVisible: item.path === selected.path,
    }));

    const overflow = overflowRef.current.map((item) => ({
      ...item,
      forceVisible: item.path === selected.path,
    }));

    commitLists(finalizeVisible(visible), overflow);
  }, [commitLists, finalizeVisible]);

  useEffect(() => {
    window.addEventListener('resize', checkOverlap);

    // Delay initial check until layout + paint are stable
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        checkOverlap();
      });
    });

    return () => window.removeEventListener('resize', checkOverlap);
  }, [checkOverlap]);

  useEffect(() => {
    if (!pinnedItem) return;

    // ALWAYS enforce forceVisible on pinned item
    const enforce = () => {
      const visible = visibleRef.current.map((item) => ({
        ...item,
        forceVisible: item.path === pinnedItem.path,
      }));

      const overflow = overflowRef.current.map((item) => ({
        ...item,
        forceVisible: item.path === pinnedItem.path,
      }));

      commitLists(finalizeVisible(visible), overflow);
      checkOverlap();
    };

    requestAnimationFrame(enforce);
  }, [pinnedItem, checkOverlap, commitLists, finalizeVisible]);

  useEffect(() => {
    const children = leftRef.current?.children;
    if (!children) return;

    Array.from(children).forEach((el) => {
      const path = el.dataset?.path;
      if (path && !itemWidthsRef.current.has(path)) {
        itemWidthsRef.current.set(path, el.getBoundingClientRect().width);
      }
    });
  }, [visibleItems]);

  return {
    visibleItems,
    overflowItems,
    selectOverflowItem,
    selectVisibleItem,
    leftRef,
    rightRef,
    logoRef,
  };
}
